/**
 * Le module **Reporting** — catalogue d'états, production sur une période,
 * export scellé, et vérification du chaînage (`EX-BO-004`, stories 12.6 à
 * 12.10).
 *
 * Maquette : artboard `b_reporting` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * `test/app/reporting-maquette.test.tsx` **relit** cette maquette et la
 * confronte au livré, plutôt que d'en recopier les valeurs ici : un attendu
 * dérivé du code éprouvé n'est pas un attendu (`D-34`).
 *
 * ## Ce que ce chantier referme
 *
 * `ReportingApi` servait **quatre routes, éprouvées, et appelées par aucun
 * écran** — la famille de défauts que `D-25`, `D-30`, `D-35`, `D-45` et `D-57`
 * décrivent. Le contrôle des routes les nommait déjà ; elles ont maintenant
 * leur écran.
 *
 * ## Le motif est « catalogue + résultat », et non « file + dossier »
 *
 * Les sept écrans livrés avant celui-ci dépilent une file : on parcourt des
 * lignes et l'on en ouvre une. Ici, rien n'attend d'être dépilé — un
 * contrôleur **choisit** un état, **pose** une période, et lit ce qui en sort.
 * Le rail de gauche est donc une `listbox` et non une table : les flèches y
 * descendent, `Entrée` choisit, et aucune consultation n'est écrite au journal
 * tant que rien n'est produit.
 *
 * ## Produire N'EST PAS lire
 *
 * C'est le point de cet écran qu'il ne faut pas manquer. `GET
 * /reporting/statements/{code}` **archive et chaîne** l'état qu'elle rend : un
 * appel produit un maillon, et un maillon ne se retire pas. L'écran ne produit
 * donc **jamais tout seul** — ni au montage, ni au choix d'un état, ni au
 * changement d'une borne. Il faut un geste, et ce geste porte le mot
 * « Produire ».
 *
 * C'est l'inverse de la conduite de `CustomersScreen`, où l'état de l'écran vit
 * dans l'adresse et où tout changement de critère relit. La même conduite ici
 * scellerait un état à chaque frappe dans un champ de date.
 *
 * ## Trois faits ne se rendent jamais seuls
 *
 *  1. **`intact` sans `linksChecked`.** Un chaînage intact sur zéro maillon est
 *     vrai et ne démontre rien — c'est la somme nulle du contrôle d'équilibre
 *     du grand livre, et c'est ce qui a laissé `D-53` vivre. L'écran affiche
 *     toujours les deux, et jamais la pastille verte seule.
 *  2. **`totalRows` sans les lignes affichées.** Le serveur rend le total ;
 *     l'écran n'en recalcule aucun. Un total recompté par le navigateur sur ce
 *     qui est à l'écran est le total de l'écran, présenté comme un fait.
 *  3. **`servedByReplica: false` sans son explication.** Ce n'est pas une
 *     panne : sans réplique configurée, l'état est produit sur la base
 *     principale — et un export lourd y prend des connexions à ce qui sert les
 *     retraits. C'est une information d'exploitation, elle se dit.
 *
 * ## L'empreinte est rendue ENTIÈRE, et à chasse fixe
 *
 * Un SHA-256 tronqué ne se confronte à rien, et un SHA-256 en police
 * proportionnelle ne se compare pas à l'œil — `a`, `c`, `e` et `f` n'y ont pas
 * la même chasse. Le jeton `--jeliko-type-font-family-mono` a été posé pour
 * cela ; sans lui, « scellé » serait un mot sur un écran.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueEntry } from '../shell/QueueShell.js';
import { useDeclareExport } from './export-context.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import {
  downloadStatement,
  fetchChainVerification,
  fetchProducedStatements,
  fetchReportDefinitions,
  produceStatement,
} from '../http/reporting.js';
import type {
  ArchivedStatement,
  ChainVerification,
  ProducedReport,
  ReportDefinition,
} from '../http/reporting.js';

/** Les colonnes de l'historique des états produits. */
const COLONNES_HISTORIQUE: readonly QueueColumn[] = [
  { id: 'reportCode', labelKey: 'reporting.columns.report', emphasis: 'strong' },
  { id: 'period', labelKey: 'reporting.columns.period' },
  { id: 'rowCount', labelKey: 'reporting.columns.rows', emphasis: 'numeric' },
  { id: 'chainIndex', labelKey: 'reporting.columns.chainIndex', emphasis: 'numeric' },
  { id: 'producedAt', labelKey: 'reporting.columns.producedAt', emphasis: 'muted' },
];

/**
 * Le module Reporting.
 *
 * Voir l'en-tête pour ce que cet écran refuse de faire tout seul.
 */
export function ReportingScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [catalogue, setCatalogue] = useState<readonly ReportDefinition[] | null>(null);
  const [erreurCatalogue, setErreurCatalogue] = useState<string | null>(null);
  const [choisi, setChoisi] = useState<string | null>(null);

  const [etat, setEtat] = useState<ProducedReport | null>(null);
  const [erreurEtat, setErreurEtat] = useState<string | null>(null);
  const [production, setProduction] = useState(false);

  const [chainage, setChainage] = useState<ChainVerification | null>(null);
  const [erreurChainage, setErreurChainage] = useState<string | null>(null);

  const [historique, setHistorique] = useState<readonly ArchivedStatement[] | null>(null);
  const [erreurHistorique, setErreurHistorique] = useState<string | null>(null);

  const [avis, setAvis] = useState<string | null>(null);

  // Le catalogue et l'historique se lisent au montage : ce sont des LECTURES,
  // elles ne scellent rien. La production, elle, attend un geste.
  useEffect(() => {
    let abandonne = false;
    void (async () => {
      try {
        const definitions = await fetchReportDefinitions(client);
        if (abandonne) return;
        setCatalogue(definitions);
        setErreurCatalogue(null);
      } catch (echec) {
        if (abandonne) return;
        // Le catalogue lu est effacé avec l'échec : le garder ferait choisir un
        // état dans une liste que le serveur vient de refuser de servir.
        setCatalogue(null);
        setErreurCatalogue(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [client]);

  const relireLHistorique = useCallback(async (): Promise<void> => {
    try {
      const produits = await fetchProducedStatements(client);
      setHistorique(produits);
      setErreurHistorique(null);
    } catch (echec) {
      setHistorique(null);
      setErreurHistorique(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client]);

  // **Une seule définition de la lecture de l'historique**, appelée au montage
  // et après chaque production. Deux copies divergeraient — c'est le défaut
  // qu'on vient de corriger côté serveur sur l'approbation d'un dossier.
  useEffect(() => {
    void relireLHistorique();
  }, [relireLHistorique]);

  const definition = useMemo(
    () => catalogue?.find((candidate) => candidate.code === choisi) ?? null,
    [catalogue, choisi],
  );

  /** Les champs de la barre de paramètres : la période, et rien d'autre. */
  const champs: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'from',
        labelKey: 'reporting.fields.from',
        control: 'date',
        placeholderKey: 'reporting.fields.fromPlaceholder',
        required: true,
      },
      {
        name: 'to',
        labelKey: 'reporting.fields.to',
        control: 'date',
        placeholderKey: 'reporting.fields.toPlaceholder',
        required: true,
      },
    ],
    [],
  );

  const produire = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      if (definition === null) return;
      setProduction(true);
      setAvis(null);
      try {
        const produit = await produceStatement(client, {
          code: definition.code,
          from: valeurs.from ?? '',
          to: valeurs.to ?? '',
        });
        setEtat(produit);
        setErreurEtat(null);
        setAvis('reporting.produced');
        // L'historique gagne un maillon À CHAQUE production : le relire ici
        // évite qu'un contrôleur voie sa propre production absente de la
        // mémoire du module, et conclue qu'elle n'a pas été archivée.
        await relireLHistorique();
      } catch (echec) {
        // L'état précédent est effacé avec l'échec. Le garder ferait lire une
        // période PRÉCÉDENTE sous les bornes nouvelles — sur un écran qui sert
        // à signer des états, c'est la pire confusion possible.
        setEtat(null);
        setErreurEtat(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      } finally {
        setProduction(false);
      }
    },
    [client, definition, relireLHistorique],
  );

  const verifier = useCallback(async (): Promise<void> => {
    try {
      const verdict = await fetchChainVerification(client);
      setChainage(verdict);
      setErreurChainage(null);
    } catch (echec) {
      setChainage(null);
      setErreurChainage(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client]);

  /**
   * L'export de la barre — **il vient du serveur**, il ne se compose pas ici.
   *
   * Les trois autres écrans exportent avec `toCsv` : ils rendent en fichier ce
   * qu'ils ont à l'écran. Ici ce serait un contresens — le fichier porterait
   * une empreinte que le serveur n'a jamais calculée, et se présenterait
   * pourtant comme un état scellé. La barre demande donc le **même** état au
   * serveur, avec `download=true`, et remet les octets tels quels.
   */
  const exportDeLaBarre = useMemo(() => {
    if (etat === null || definition === null) return null;
    return () => {
      void (async () => {
        try {
          const octets = await downloadStatement(client, {
            code: etat.reportCode,
            from: etat.periodStart,
            to: etat.periodEnd,
          });
          remettre(`${etat.reportCode}-${etat.periodStart}-${etat.periodEnd}`, octets.blob);
          setAvis('reporting.exported');
        } catch (echec) {
          setErreurEtat(echec instanceof ApiError ? echec.messageKey : 'errors.server');
        }
      })();
    };
  }, [client, definition, etat]);
  useDeclareExport(exportDeLaBarre);

  const lignesHistorique: readonly QueueEntry[] = useMemo(
    () =>
      (historique ?? []).map((produit) => ({
        id: produit.id,
        reference: produit.id,
        values: [
          produit.reportCode,
          `${produit.periodStart} — ${produit.periodEnd}`,
          String(produit.rowCount),
          String(produit.chainIndex),
          produit.producedAt,
        ],
      })),
    [historique],
  );

  return (
    <section aria-label={t('reporting.landmark')} className="reporting">
      <p className="reporting__journal-notice">{t('reporting.journalNotice')}</p>

      {avis === null ? null : (
        <p className="reporting__avis" role="status">
          {t(avis)}
        </p>
      )}

      <div className="reporting__work">
        <ReportCatalogue
          catalogue={catalogue}
          chosen={choisi}
          error={erreurCatalogue}
          onChoose={setChoisi}
        />

        <div className="reporting__result">
          {definition === null ? (
            <SystemState
              bodyKey="reporting.chooseBody"
              testId="reporting-choose"
              titleKey="reporting.chooseTitle"
            />
          ) : (
            <>
              <div className="reporting__parameters">
                <h2 className="reporting__title" data-testid="reporting-title">
                  {definition.label}
                </h2>
                <p className="reporting__meta">
                  {t('reporting.definitionMeta', {
                    format: definition.formatCode,
                    periodicity: definition.periodicity,
                  })}
                </p>
                <FormShell
                  fields={champs}
                  layout="inline"
                  onSubmit={produire}
                  submitLabelKey="reporting.produce"
                />
              </div>

              {erreurEtat === null ? null : (
                <p className="reporting__notice" role="alert">
                  {t(erreurEtat)}
                </p>
              )}

              {etat === null ? (
                erreurEtat === null && !production ? (
                  <SystemState
                    bodyKey="reporting.beforeQueryBody"
                    testId="reporting-before-query"
                    titleKey="reporting.beforeQueryTitle"
                  />
                ) : null
              ) : (
                <ProducedStatement report={etat} />
              )}
            </>
          )}
        </div>
      </div>

      <ChainPanel
        error={erreurChainage}
        onVerify={() => {
          void verifier();
        }}
        verdict={chainage}
      />

      <section aria-label={t('reporting.historyLandmark')} className="reporting__history">
        <h2 className="reporting__section-title">{t('reporting.historyTitle')}</h2>
        {erreurHistorique === null ? null : (
          <p className="reporting__notice" role="alert">
            {t(erreurHistorique)}
          </p>
        )}
        {historique === null ? (
          erreurHistorique === null ? (
            <p className="reporting__idle">{t('reporting.loading')}</p>
          ) : null
        ) : lignesHistorique.length === 0 ? (
          <SystemState
            bodyKey="reporting.historyEmptyBody"
            testId="reporting-history-empty"
            titleKey="reporting.historyEmptyTitle"
          />
        ) : (
          <QueueShell columns={COLONNES_HISTORIQUE} entries={lignesHistorique} />
        )}
      </section>
    </section>
  );
}

/**
 * Le rail de catalogue — une `listbox`, et non une table.
 *
 * Rien n'y est dépilé : on choisit. Les flèches descendent, `Entrée` choisit,
 * et `aria-activedescendant` dit au lecteur d'écran où l'on est. C'est la
 * conduite déjà écrite pour les options de filtre.
 */
function ReportCatalogue({
  catalogue,
  chosen,
  error,
  onChoose,
}: {
  readonly catalogue: readonly ReportDefinition[] | null;
  readonly chosen: string | null;
  readonly error: string | null;
  readonly onChoose: (code: string) => void;
}): ReactNode {
  const { t } = useTranslation();

  if (error !== null) {
    return (
      <div className="reporting__catalogue">
        <p className="reporting__notice" role="alert">
          {t(error)}
        </p>
      </div>
    );
  }
  if (catalogue === null) {
    return (
      <div className="reporting__catalogue">
        <p className="reporting__idle">{t('reporting.loading')}</p>
      </div>
    );
  }
  if (catalogue.length === 0) {
    return (
      <div className="reporting__catalogue">
        <SystemState
          bodyKey="reporting.catalogueEmptyBody"
          testId="reporting-catalogue-empty"
          titleKey="reporting.catalogueEmptyTitle"
        />
      </div>
    );
  }

  return (
    <div className="reporting__catalogue">
      <h2 className="reporting__section-title">{t('reporting.catalogueTitle')}</h2>
      <ul
        aria-activedescendant={chosen === null ? undefined : `reporting-option-${chosen}`}
        aria-label={t('reporting.catalogueTitle')}
        className="reporting__options"
        data-testid="reporting-catalogue"
        role="listbox"
        tabIndex={0}
        onKeyDown={(evenement) => {
          const codes = catalogue.map((definition) => definition.code);
          const rang = chosen === null ? -1 : codes.indexOf(chosen);
          if (evenement.key === 'ArrowDown') {
            evenement.preventDefault();
            onChoose(codes[Math.min(rang + 1, codes.length - 1)] ?? codes[0]);
          } else if (evenement.key === 'ArrowUp') {
            evenement.preventDefault();
            onChoose(codes[Math.max(rang - 1, 0)] ?? codes[0]);
          }
        }}
      >
        {catalogue.map((definition) => (
          <li
            aria-selected={definition.code === chosen}
            className="reporting__option"
            data-chosen={definition.code === chosen}
            id={`reporting-option-${definition.code}`}
            key={definition.code}
            onClick={() => {
              onChoose(definition.code);
            }}
            role="option"
          >
            <span className="reporting__option-name">{definition.label}</span>
            <span className="reporting__option-meta">
              {t('reporting.optionMeta', {
                columns: definition.columns.length,
                periodicity: definition.periodicity,
              })}
            </span>
            {definition.active ? null : (
              <span className="reporting__badge" data-tone="warning">
                {t('reporting.inactive')}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** L'état produit : sa réponse en gros, sa table, puis son pied de preuve. */
function ProducedStatement({ report }: { readonly report: ProducedReport }): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="reporting__statement" data-testid="reporting-statement">
      <p className="reporting__answer" data-testid="reporting-total">
        {t('reporting.totalRows', { rows: report.totalRows })}
      </p>
      <p className="reporting__meta">
        {t('reporting.statementMeta', {
          from: report.periodStart,
          to: report.periodEnd,
          format: report.formatCode,
        })}
      </p>

      {report.notices.map((avis) => (
        <p className="reporting__notice" data-tone="info" key={avis} role="status">
          {avis}
        </p>
      ))}

      {report.rows.length === 0 ? (
        <SystemState
          bodyKey="reporting.emptyBody"
          testId="reporting-empty"
          titleKey="reporting.emptyTitle"
        />
      ) : (
        <div className="reporting__table-frame">
          <table className="reporting__table">
            <caption className="shell__reading">{t('reporting.tableCaption')}</caption>
            <thead>
              <tr>
                {report.headers.map((entete) => (
                  <th key={entete} scope="col">
                    {entete}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* **La clé est le RANG, et c'est le seul choix juste ici.** Un
                  état n'a aucune clé naturelle : deux lignes peuvent être
                  identiques et le sont légitimement — deux écritures du même
                  montant le même jour. Composer une clé à partir du contenu
                  ferait s'effondrer ces deux lignes en une, et l'état
                  afficherait moins de lignes qu'il n'en porte. La table est
                  d'ailleurs remplacée en entier à chaque production : aucun
                  état de composant ne voyage entre deux rendus. */}
              {report.rows.map((ligne, rang) => (
                <tr key={`ligne-${String(rang)}`}>
                  {ligne.map((cellule, colonne) => (
                    <td data-emphasis="numeric" key={`cellule-${String(colonne)}`}>
                      {cellule}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <dl className="reporting__proof">
        <div className="reporting__proof-row">
          <dt>{t('reporting.proof.sha')}</dt>
          <dd className="reporting__hash" data-testid="reporting-sha">
            {report.contentSha256}
          </dd>
        </div>
        <div className="reporting__proof-row">
          <dt>{t('reporting.proof.rowHash')}</dt>
          <dd className="reporting__hash">{report.rowHash}</dd>
        </div>
        <div className="reporting__proof-row">
          <dt>{t('reporting.proof.chainIndex')}</dt>
          <dd>{report.chainIndex}</dd>
        </div>
        <div className="reporting__proof-row">
          <dt>{t('reporting.proof.retention')}</dt>
          <dd>{report.retentionUntil}</dd>
        </div>
        <div className="reporting__proof-row">
          <dt>{t('reporting.proof.source')}</dt>
          {/* `false` n'est PAS une panne, et ne se rend pas comme telle : voir
              l'en-tête du fichier. */}
          <dd data-testid="reporting-source">
            {report.servedByReplica ? t('reporting.proof.replica') : t('reporting.proof.primary')}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * Le verdict du chaînage.
 *
 * **La pastille ne se rend jamais seule.** Voir l'en-tête, point 1 : un
 * chaînage intact sur zéro maillon est vrai et ne démontre rien.
 */
function ChainPanel({
  error,
  onVerify,
  verdict,
}: {
  readonly error: string | null;
  readonly onVerify: () => void;
  readonly verdict: ChainVerification | null;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <section aria-label={t('reporting.chainLandmark')} className="reporting__chain">
      <h2 className="reporting__section-title">{t('reporting.chainTitle')}</h2>
      <p className="reporting__meta">{t('reporting.chainBody')}</p>
      <button className="reporting__verify" onClick={onVerify} type="button">
        {t('reporting.verify')}
      </button>
      {error === null ? null : (
        <p className="reporting__notice" role="alert">
          {t(error)}
        </p>
      )}
      {verdict === null ? null : (
        <p
          className="reporting__verdict"
          data-testid="reporting-chain"
          data-tone={verdict.intact ? 'success' : 'danger'}
          role="status"
        >
          {verdict.intact
            ? t('reporting.chainIntact', { links: verdict.linksChecked, head: verdict.headIndex })
            : t('reporting.chainBroken', {
                index: verdict.brokenAtIndex ?? verdict.headIndex,
                links: verdict.linksChecked,
              })}
        </p>
      )}
    </section>
  );
}

/**
 * Un état de zone — invite, vide, ou rien encore demandé.
 *
 * **Trois faits différents ne se rendent pas du même `<p>` gris.** C'est le
 * reproche que le back-office s'est fait à lui-même : devant une zone lente,
 * l'opérateur ne distinguait pas « ça arrive » de « il n'y a rien », et
 * attendait un contenu qui ne viendrait pas. Les jetons
 * `--jeliko-component-system-state-*` existaient et n'étaient employés nulle
 * part ; ils le sont ici.
 */
function SystemState({
  bodyKey,
  testId,
  titleKey,
}: {
  readonly bodyKey: string;
  readonly testId: string;
  readonly titleKey: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="reporting__state" data-testid={testId}>
      <span aria-hidden="true" className="reporting__state-emblem" />
      <p className="reporting__state-title">{t(titleKey)}</p>
      <p className="reporting__state-body">{t(bodyKey)}</p>
    </div>
  );
}

/**
 * Remet les octets du serveur au navigateur.
 *
 * Distinct de `remettreLeFichier` de `export-csv.ts`, et pour une raison qui
 * n'est pas cosmétique : celui-là compose un `Blob` à partir d'une **chaîne**
 * que l'écran a fabriquée. Ici les octets viennent du serveur, scellés, et les
 * retoucher — ne serait-ce qu'en les relisant en texte — ferait diverger le
 * fichier de son empreinte.
 */
function remettre(nom: string, octets: Blob): boolean {
  if (typeof URL.createObjectURL !== 'function') return false;
  const adresse = URL.createObjectURL(octets);
  const ancre = document.createElement('a');
  ancre.href = adresse;
  ancre.download = nom;
  document.body.append(ancre);
  ancre.click();
  ancre.remove();
  URL.revokeObjectURL(adresse);
  return true;
}
